import { useForm } from '@mantine/form'
import { TradeEntryFormPresentation } from './trade-entry-form-presentation'
import { useDispatch, useSelector } from 'react-redux'
import { getFacilities } from 'app/state/ducks/facilities/selectors'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { useEffect, useState } from 'react'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { formatDateToUTC } from 'app/utils/util-functions'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { loadAllFacilities } from 'app/state/ducks/facilities/thunks'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import { loadAllEntityTypes } from 'app/state/ducks/entity-type/thunks'
import { approveTrade, createDraftTrade, rejectTrade, submitTrade } from './trade-daml-actions'
import { useNavigate } from 'react-router'
import { ErrorNotification } from 'app/views/components/notifications/notification'

export default function TradeEntryFormLogic() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        dispatch(loadAllFacilities())
        dispatch(loadAllDeals())
        dispatch(loadEntities())
        dispatch(loadAllEntityProfiles())
        dispatch(loadAllEntityTypes())
    }, [])

    const facilities = useSelector(getFacilities)
    const deals = useSelector(getDeals)
    const entities = useSelector(getEntities)

    const form: any = useForm({
        initialValues: {
            id: undefined,
            clientId: null,
            trader: null,
            pm: null,
            desk: null,
            closer: null,
            counterpartyCloser: null,
            tradeGroupId: '',
            sellerId: '',
            buyerId: '',
            myEntity: '',
            seller: null,
            buyer: null,
            dealId: '',
            deal: null,
            tradeDate: null,
            tradeType: null,
            tradeAssociation: null,
            transferType: null,
            interestTreatment: null,
            assignmentFeePayType: null,
            settlementMethod: null,
            outsideTradeId: null,
            tradeDocsType: null,
            includeDelayCompensation: true,
            disableDCCoC: false,
            paymentType: null,
            delayCompensationDate: null,
            tradedFacilities: [],
            systemId: null,
        },
        validate: {
            settlementMethod: (value: string | null) =>
                !value ? 'Settlement method is required!' : null,
            paymentType: (value: string | null) =>
                !value ? 'Payment type is required!' : null,
        },
        transformValues: (values): MasterTradeParams => {
            const seller = entities.find((entity: any) => entity.id === values.sellerId)
            const buyer = entities.find((entity: any) => entity.id === values.buyerId)
            const deal = deals.find((deal: any) => deal.id === values.dealId)
            const tradeDate = values.tradeDate ? formatDateToUTC(values.tradeDate) : null
            const delayCompensationDate = !values.includeDelayCompensation
                ? null
                : values.delayCompensationDate
                    ? formatDateToUTC(values.delayCompensationDate)
                    : tradeDate

            const tradedFacilities = values.tradedFacilities.map((tf: any) => {
                const facility = facilities.find((f: any) => f.id === tf.facilityId);
                return {
                    facilityId: { id: facility?.id ?? '', admin: facility?.accountManagementAdmin ?? '' },
                    tradeAmount: tf.tradeAmount,
                    price: tf.price,
                    facilityAmount: '' + (facility?.amount ?? '0.0'),
                    facilityDetails: null,
                    fees: [],
                }
            })


            return {
                ...values,
                myEntity: values.myEntity,
                delayCompensationDate: delayCompensationDate,
                buyerParty: "",
                sellerParty: "",
                tradeDate: tradeDate,
                createDate: null,
                sellerId: { id: seller?.id ?? '', admin: seller?.maker ?? '' },
                buyerId: { id: buyer?.id ?? '', admin: buyer?.maker ?? '' },
                dealId: { id: deal?.id ?? '', admin: deal?.accountManagementAdmin ?? '' },
                facilityIds: tradedFacilities.map((tf: any) => tf.facilityId),
                tradedFacilities: tradedFacilities,
                holds: [],
            }
        },
    })

    const createTradeEntry = async () => {
        const validation = form.validate()
        if (validation.hasErrors) {
            if (validation.errors.settlementMethod) {
                ErrorNotification({ title: 'Validation Error', message: validation.errors.settlementMethod })
            }
            if (validation.errors.paymentType) {
                ErrorNotification({ title: 'Validation Error', message: validation.errors.paymentType })
            }
            return
        }
        try {
            setLoading(true)
            const values = form.getTransformedValues()
            const result = await createDraftTrade(values, dispatch, true)
            if (!result) {
                return
            }
            navigate('/tradeentry/' + result.id)
            form.reset()
        }
        finally {
            setLoading(false)
        }
    }

    const createApprovedTradeEntry = async () => {
        const validation = form.validate()
        if (validation.hasErrors) {
            if (validation.errors.settlementMethod) {
                ErrorNotification({ title: 'Validation Error', message: validation.errors.settlementMethod })
            }
            if (validation.errors.paymentType) {
                ErrorNotification({ title: 'Validation Error', message: validation.errors.paymentType })
            }
            return
        }
        try {
            setLoading(true)
            const values = form.getTransformedValues()
            const result = await createDraftTrade(values, dispatch)
            if (!result) {
                return
            }

            form.setValues({
                ...form.values,
                systemId: result.systemId,
                id: result.id,
                delayCompensationDate: result.delayCompensationDate,
            })

            const submitResult = await submitTrade(result, dispatch)
            if (!submitResult) {
                return
            }

            const approvalResult = await approveTrade(result, dispatch, true)
            if (!approvalResult) {
                await rejectTrade(result, dispatch)
                return
            }
            navigate('/tradeentry/' + result.id)
            form.reset()
        }
        finally {
            setLoading(false)
        }
    }

    const cancelTradeEntry = () => {
        navigate('/tradeentry')
    }

    const onDealChange = (value?: string | null) => {

        if (!value) {
            form.setFieldValue('dealId', '')
            form.setFieldValue('deal', null)
            form.setFieldValue('tradedFacilities', [])
            return
        }
        const deal = deals.find((deal: any) => deal.id === value)
        if (!deal) {
            form.setFieldValue('dealId', '')
            form.setFieldValue('deal', null)
            form.setFieldValue('tradedFacilities', [])
            return
        }

        form.setFieldValue('dealId', deal.id)
        form.setFieldValue('deal', deal)

        const dealFacilities = facilities.filter((facility: any) => facility.dealId.id === value)
        const facilityValues = dealFacilities.map((facility: any) => ({
            facilityId: facility.id,
            tradeAmount: 0.0,
            price: 0.0,
            currentAmount: 0.0,
            facility: facility,
        })).sort((a, b) => {
            const nameA = a.facility?.name?.toLowerCase() || ''
            const nameB = b.facility?.name?.toLowerCase() || ''
            return nameA.localeCompare(nameB)
        })
        form.setFieldValue('tradedFacilities', facilityValues)
    }

    const onBuyerSellerChange = (value: any) => {
        form.setFieldValue('myEntity', value)
        if (!form.values.myEntity && value === 'Buy') {
            return
        }
        form.setFieldValue('sellerId', form.values.buyerId)
        form.setFieldValue('buyerId', form.values.sellerId)
        form.setFieldValue('seller', form.values.buyer)
        form.setFieldValue('buyer', form.values.seller)
    }

    return (
        <TradeEntryFormPresentation form={form} loading={loading} onDealChange={onDealChange} onBuyerSellerChange={onBuyerSellerChange} createTradeEntry={createTradeEntry} cancelTradeEntry={cancelTradeEntry} createApprovedTradeEntry={createApprovedTradeEntry} />
    )
}